import { create } from 'zustand'
import { persist, devtools } from 'zustand/middleware'

// Define user roles for RBAC (can be extended based on branchUserRoleId)
export type UserRole = 'Admin' | 'Finance' | 'HR' | 'Employee' | 'Customer'

export interface Branch {
  branchId: number
  branchName: string
  branchUserRoleId: number
}

export interface User {
  userId: number
  userName: string
  email: string
  mobile: string
  isActive: boolean
  selectedBranch?: Branch
  branches: Branch[]
}

export interface AuthState {
  // Authentication state
  token: string | null
  tokenExpires: string | null
  user: User | null
  isAuthenticated: boolean
  isLoading: boolean
  
  // Branch selection state
  needsBranchSelection: boolean
  availableBranches: Branch[]

  // Actions
  login: (token: string, expires: string, userData: Omit<User, 'selectedBranch'>) => void
  logout: () => void
  setLoading: (loading: boolean) => void
  updateUser: (user: Partial<User>) => void
  selectBranch: (branch: Branch) => void
  switchBranch: () => void  // New function to switch branch
  setNeedsBranchSelection: (needs: boolean) => void
  hasRole: (role: UserRole) => boolean
  hasAnyRole: (roles: UserRole[]) => boolean
  isTokenExpired: () => boolean
}

/**
 * Authentication store using Zustand with persistence
 * Manages user authentication state, JWT tokens, and branch selection
 */
export const useAuthStore = create<AuthState>()(
  devtools(
    persist(
      (set, get) => ({
        // Initial state
        token: null,
        tokenExpires: null,
        user: null,
        isAuthenticated: false,
        isLoading: false,
        needsBranchSelection: false,
        availableBranches: [],

        // Login action - sets tokens and user data
        login: (token: string, expires: string, userData: Omit<User, 'selectedBranch'>) => {
          set(
            {
              token,
              tokenExpires: expires,
              user: userData,
              isAuthenticated: false, // Will be true only after branch selection
              isLoading: false,
              needsBranchSelection: true,
              availableBranches: userData.branches,
            },
            false,
            'auth/login'
          )
        },

        // Logout action - clears all auth data
        logout: () => {
          set(
            {
              token: null,
              tokenExpires: null,
              user: null,
              isAuthenticated: false,
              isLoading: false,
              needsBranchSelection: false,
              availableBranches: [],
            },
            false,
            'auth/logout'
          )
        },

        // Set loading state for auth operations
        setLoading: (loading: boolean) => {
          set({ isLoading: loading }, false, 'auth/setLoading')
        },

        // Update user information
        updateUser: (userUpdate: Partial<User>) => {
          const currentUser = get().user
          if (currentUser) {
            set(
              { user: { ...currentUser, ...userUpdate } },
              false,
              'auth/updateUser'
            )
          }
        },

        // Select branch and complete authentication
        selectBranch: (branch: Branch) => {
          const currentUser = get().user
          if (currentUser) {
            set(
              {
                user: { ...currentUser, selectedBranch: branch },
                isAuthenticated: true,
                needsBranchSelection: false,
              },
              false,
              'auth/selectBranch'
            )
          }
        },

        // Switch branch - go back to branch selection
        switchBranch: () => {
          const currentUser = get().user
          if (currentUser) {
            set(
              {
                user: { ...currentUser, selectedBranch: undefined },
                isAuthenticated: false,
                needsBranchSelection: true,
                availableBranches: currentUser.branches,
              },
              false,
              'auth/switchBranch'
            )
          }
        },

        // Set branch selection requirement
        setNeedsBranchSelection: (needs: boolean) => {
          set({ needsBranchSelection: needs }, false, 'auth/setNeedsBranchSelection')
        },

        // Check if user has specific role (based on branchUserRoleId)
        hasRole: (role: UserRole) => {
          const user = get().user
          if (!user?.selectedBranch) return false

          const roleMap: Record<number, UserRole[]> = {
            1: ['Admin'],
            2: ['Finance'],
            3: ['HR'],
            4: ['Employee'],
            5: ['Customer'],
          }

          const id = Number(user.selectedBranch.branchUserRoleId)
          const userRoles =
            Number.isFinite(id) && roleMap[id]?.length ? roleMap[id]! : ['Admin']
          return userRoles.includes(role)
        },

        // Check if user has any of the specified roles
        hasAnyRole: (roles: UserRole[]) => {
          return roles.some(role => get().hasRole(role))
        },

        // Check if token is expired
        isTokenExpired: () => {
          const { tokenExpires } = get()
          if (!tokenExpires) return true
          return new Date() >= new Date(tokenExpires)
        },
      }),
      {
        name: 'auth-storage',
        storage: {
          getItem: (name: string) => {
            const value = sessionStorage.getItem(name)
            return value ? JSON.parse(value) : null
          },
          setItem: (name: string, value: any) => {
            sessionStorage.setItem(name, JSON.stringify(value))
          },
          removeItem: (name: string) => {
            sessionStorage.removeItem(name)
          },
        },
        // Persist authentication data
        partialize: (state) => ({
          token: state.token,
          tokenExpires: state.tokenExpires,
          user: state.user,
          isAuthenticated: state.isAuthenticated,
          needsBranchSelection: state.needsBranchSelection,
          availableBranches: state.availableBranches,
        }),
      }
    ),
    {
      name: 'auth-store',
    }
  )
) 